import React, { useCallback, useEffect, useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import { FontSize } from './extensions/fontSize'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link as LinkIcon,
  ChevronUp,
  ChevronDown,
} from 'lucide-react'
import './customRichTextEditor.css'

export type ToolbarFeature =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'bulletList'
  | 'orderedList'
  | 'link'
  | 'fontSize'
  | 'textColor'

interface CustomRichTextEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  height?: string
  toolbarFeatures?: ToolbarFeature[]
}

interface ToolbarButton {
  feature: ToolbarFeature
  icon: React.ComponentType<{ className?: string }>
  onClick: () => void
  isActive: boolean
  tooltip: string
  'aria-label': string
}

const DEFAULT_CURRENT_COLOUR = '#000000'
const DEFAULT_CURRENT_FONT_SIZE = 16

const CustomRichTextEditor: React.FC<CustomRichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter description...',
  className,
  height = '120px',
  toolbarFeatures,
}) => {
  const [currentColor, setCurrentColor] = useState(DEFAULT_CURRENT_COLOUR)
  const [currentFontSize, setCurrentFontSize] = useState(
    DEFAULT_CURRENT_FONT_SIZE
  )

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle.configure({
        HTMLAttributes: {
          class: 'custom-text-style',
        },
      }),
      FontSize,
      Color,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-ss-blue-500 underline cursor-pointer',
        },
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
      const color = editor.getAttributes('textStyle').color
      if (color) {
        setCurrentColor(color)
      } else {
        setCurrentColor(DEFAULT_CURRENT_COLOUR)
      }
      const fontSize = editor.getAttributes('textStyle').fontSize
      if (fontSize) {
        const size = Number.parseInt(fontSize.replace('px', ''))
        setCurrentFontSize(size)
      } else {
        setCurrentFontSize(DEFAULT_CURRENT_FONT_SIZE)
      }
    },
    onSelectionUpdate: ({ editor }) => {
      const color = editor.getAttributes('textStyle').color
      if (color) {
        setCurrentColor(color)
      } else {
        setCurrentColor(DEFAULT_CURRENT_COLOUR)
      }

      const fontSize = editor.getAttributes('textStyle').fontSize
      if (fontSize) {
        const size = Number.parseInt(fontSize.replace('px', ''))
        setCurrentFontSize(size)
      } else {
        setCurrentFontSize(DEFAULT_CURRENT_FONT_SIZE)
      }
    },
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm max-w-none focus:outline-none p-3 overflow-wrap-anywhere word-break-break-word',
          `min-h-[${height}]`
        ),
      },
    },
  })

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      const { from, to } = editor.state.selection
      editor.commands.setContent(value)
      if (from === to) {
        try {
          editor.commands.setTextSelection({ from, to })
        } catch {
          editor.commands.focus()
        }
      }
    }

    if (!value || value === '<p></p>') {
      setCurrentColor(DEFAULT_CURRENT_COLOUR)
      setCurrentFontSize(DEFAULT_CURRENT_FONT_SIZE)
    }
  }, [editor, value])

  // Memoized action handlers to prevent unnecessary re-renders
  const toggleBold = useCallback(() => {
    editor?.chain().focus().toggleBold().run()
  }, [editor])

  const toggleItalic = useCallback(() => {
    editor?.chain().focus().toggleItalic().run()
  }, [editor])

  const toggleUnderline = useCallback(() => {
    editor?.chain().focus().toggleUnderline().run()
  }, [editor])

  const toggleBulletList = useCallback(() => {
    editor?.chain().focus().toggleBulletList().run()
  }, [editor])

  const toggleOrderedList = useCallback(() => {
    editor?.chain().focus().toggleOrderedList().run()
  }, [editor])

  const setLink = useCallback(() => {
    if (!editor) return

    const previousUrl = editor.getAttributes('link').href
    const url = window.prompt('URL', previousUrl)

    if (url === null) return

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }, [editor])

  const increaseFontSize = useCallback(() => {
    if (!editor) return
    const newSize = currentFontSize + 2
    setCurrentFontSize(newSize)
    editor.chain().focus().setFontSize(`${newSize}px`).run()
  }, [editor, currentFontSize])

  const decreaseFontSize = useCallback(() => {
    if (!editor) return
    const newSize = Math.max(8, currentFontSize - 2)
    setCurrentFontSize(newSize)
    editor.chain().focus().setFontSize(`${newSize}px`).run()
  }, [editor, currentFontSize])

  const setTextColor = useCallback(
    (color: string) => {
      if (!editor) return
      setCurrentColor(color)
      editor.chain().focus().setColor(color).run()
    },
    [editor]
  )

  if (!editor) {
    return null
  }

  const allToolbarButtons: ToolbarButton[] = [
    {
      feature: 'bold',
      icon: Bold,
      onClick: toggleBold,
      isActive: editor.isActive('bold'),
      tooltip: 'Bold (Ctrl+B)',
      'aria-label': 'Toggle bold formatting',
    },
    {
      feature: 'italic',
      icon: Italic,
      onClick: toggleItalic,
      isActive: editor.isActive('italic'),
      tooltip: 'Italic (Ctrl+I)',
      'aria-label': 'Toggle italic formatting',
    },
    {
      feature: 'underline',
      icon: UnderlineIcon,
      onClick: toggleUnderline,
      isActive: editor.isActive('underline'),
      tooltip: 'Underline (Ctrl+U)',
      'aria-label': 'Toggle underline formatting',
    },
    {
      feature: 'bulletList',
      icon: List,
      onClick: toggleBulletList,
      isActive: editor.isActive('bulletList'),
      tooltip: 'Bullet List',
      'aria-label': 'Toggle bullet list',
    },
    {
      feature: 'orderedList',
      icon: ListOrdered,
      onClick: toggleOrderedList,
      isActive: editor.isActive('orderedList'),
      tooltip: 'Numbered List',
      'aria-label': 'Toggle numbered list',
    },
    {
      feature: 'link',
      icon: LinkIcon,
      onClick: setLink,
      isActive: editor.isActive('link'),
      tooltip: 'Add Link',
      'aria-label': 'Add or edit link',
    },
  ]

  const visibleButtons = toolbarFeatures
    ? allToolbarButtons.filter(button =>
        toolbarFeatures.includes(button.feature)
      )
    : allToolbarButtons

  const showFontSizeControls = toolbarFeatures?.includes('fontSize') ?? false
  const showColorControls = toolbarFeatures?.includes('textColor') ?? false
  const showToolbar =
    visibleButtons.length > 0 || showFontSizeControls || showColorControls

  return (
    <div
      className={cn(
        'border border-ss-neutral-100 rounded-lg overflow-hidden relative w-full max-w-full',
        className
      )}
      role="textbox"
      aria-label="Rich text editor"
    >
      {/* Editor */}
      <div
        className={cn(
          'bg-white relative overflow-auto w-full max-w-full',
          showToolbar ? 'rounded-t-lg' : 'rounded-lg'
        )}
        style={{ height: height }}
      >
        <EditorContent
          editor={editor}
          className="tiptap-editor w-full max-w-full"
        />
        {!value && (
          <div
            className="absolute top-3 left-3 text-ss-neutral-400 pointer-events-none text-sm"
            aria-hidden="true"
          >
            {placeholder}
          </div>
        )}
      </div>

      {showToolbar && (
        <div
          className="flex gap-1 bg-ss-neutral-25 rounded-b-lg p-2 border-t border-ss-neutral-100"
          role="toolbar"
          aria-label="Text formatting options"
        >
          {/* Font Size Controls */}
          {showFontSizeControls && (
            <div className="flex gap-2 items-center">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2 rounded transition-colors custom-rich-text-editor-button flex items-center gap-0.5 mr-1 relative"
                onClick={increaseFontSize}
                title="Increase font size"
                aria-label="Increase font size"
              >
                <span className="font-bold text-sm mr-3.5">A</span>
                <ChevronUp
                  className="size-[14px] absolute top-[2px] right-0"
                  aria-hidden="true"
                />
              </Button>
              <span className="font-semibold text-sm text-ss-neutral-400 mr-3">{`${currentFontSize}px`}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2 rounded transition-colors custom-rich-text-editor-button flex items-center gap-0.5 relative"
                onClick={decreaseFontSize}
                title="Decrease font size"
                aria-label="Decrease font size"
              >
                <span className="font-bold text-sm mr-3.5">A</span>
                <ChevronDown
                  className="size-[14px] absolute top-[2px] right-0"
                  aria-hidden="true"
                />
              </Button>
            </div>
          )}

          {/* Text Color Picker */}
          {showColorControls && (
            <div className="relative mr-2 border-l border-r border-ss-neutral-100 px-2">
              <div className="flex flex-col items-center">
                <input
                  type="color"
                  value={currentColor}
                  onChange={e => setTextColor(e.target.value)}
                  className="absolute opacity-0 w-8 h-8 cursor-pointer"
                  title="Text color"
                  aria-label="Change text color"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 rounded transition-colors custom-rich-text-editor-button flex flex-col items-center justify-center relative pointer-events-none gap-0"
                  title="Text color"
                  aria-label="Change text color"
                >
                  <span className="font-bold text-sm leading-none">A</span>
                  <div
                    className="h-0.5 w-4 rounded mt-0.5"
                    style={{ backgroundColor: currentColor }}
                    aria-hidden="true"
                  />
                </Button>
              </div>
            </div>
          )}

          {/* Standard Formatting Buttons */}
          {visibleButtons.map(button => {
            const Icon = button.icon
            return (
              <Button
                key={button.feature}
                type="button"
                variant="ghost"
                size="sm"
                className={cn(
                  'h-8 w-8 p-0 rounded transition-colors',
                  'custom-rich-text-editor-button '
                )}
                onClick={e => {
                  if (!button.isActive) {
                    e.currentTarget.classList.add(
                      'custom-rich-text-editor-button-is-active'
                    )
                  } else {
                    e.currentTarget.classList.remove(
                      'custom-rich-text-editor-button-is-active'
                    )
                  }
                  button.onClick()
                }}
                title={button.tooltip}
                aria-label={button['aria-label']}
                aria-pressed={button.isActive}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
              </Button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default CustomRichTextEditor
